<script setup>

import { ref, computed, onMounted } from 'vue';
import { required, helpers, sameAs, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import store from '../../state/store';

const state = ref({
  temporary_password: '',
  old_password: '',
  password: '',
  password_confirmation: ''
});

const temporaryPasswordInputType = ref('password');
const oldPasswordInputType = ref('password');
const newPasswordInputType = ref('password');
const confirmPasswordInputType = ref('password');

const notification = computed(() => store.state.notification);
const loading = computed(() => store.state.globals.loading)

const user = computed(() => JSON.parse(localStorage.getItem('user')));
const temp_password = computed(() => JSON.parse(localStorage.getItem('temp_password')));

const rules = computed(() => ({
   temporary_password: {
     requiredIfFunction: helpers.withMessage("Temporary Password is required", requiredIf(() => user.value.is_password_forgot === 1))
   },
   old_password: {
     requiredIfFunction: helpers.withMessage("Old Password is required", requiredIf(() => user.value.is_password_forgot === 0))
   },
   password: {
     required: helpers.withMessage("Password is required", required),
   },
   password_confirmation: {
     required: helpers.withMessage("Password Confirmation is required", required),
     sameAsPassword: helpers.withMessage("Password Confirmation must be the same as Password", sameAs(state.value.password))
   }
}))

const submitted = ref(false);
const v$ = useVuelidate(rules, state);

const changePassword = async () => {
  submitted.value =  true;
  v$.value.$touch();

  if (v$.value.$invalid) return;

  try {
    await store.dispatch('auth/changePassword', state.value);
    state.value.password = '';
    state.value.password_confirmation = '';
    v$.value.$reset();
  } catch (err) {
    console.log(err);
  }
}

const showTemporaryPassword = () => {
    if (temporaryPasswordInputType.value === 'password') {
      return temporaryPasswordInputType.value = 'text';
    }
    return temporaryPasswordInputType.value = 'password';
}

const showOldPassword = () => {
    if (oldPasswordInputType.value === 'password') {
      return oldPasswordInputType.value = 'text';
    }
    return oldPasswordInputType.value = 'password';
}

const showNewPassword = () => {
    if (newPasswordInputType.value === 'password') {
      return newPasswordInputType.value = 'text';
    }
    return newPasswordInputType.value = 'password';
}

const showConfirmPassword = () => {
    if (confirmPasswordInputType.value === 'password') {
      return confirmPasswordInputType.value = 'text';
    }
    return confirmPasswordInputType.value = 'password';
}

onMounted(() => {
  store.dispatch('notification/clear', null, { root: true});

  if (user.value && user.value.is_password_forgot === 1) {
    state.value.temporary_password = temp_password.value.temp_password
  }
})
</script>

<template>
  <div class="auth-page-wrapper pt-5">
    <!-- auth page bg -->
    <div class="auth-one-bg-position auth-one-bg" id="auth-particles">
      <div class="bg-overlay"></div>

      <div class="shape">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 1440 120"
        >
          <path
            d="M 0,36 C 144,53.6 432,123.2 720,124 C 1008,124.8 1296,56.8 1440,40L1440 140L0 140z"
          ></path>
        </svg>
      </div>
    </div>

    <!-- auth page content -->
    <div class="auth-page-content">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center mt-sm-5 mb-4 text-white-50">
              <div>
                <router-link to="/" class="d-inline-block auth-logo">
                  <img
                    src="@/assets/images/qpax/logo.png"
                    alt=""
                    height="200"
                  />
                </router-link>
              </div>
              <p class="mt-3 fs-15 fw-medium">
                Traffic Citation Management System
              </p>
            </div>
          </div>
        </div>
        <!-- end row -->

        <div class="row justify-content-center">
          <div class="col-md-8 col-lg-6 col-xl-5">
            <b-overlay :show="loading">
              <div class="card mt-4">
                <div class="card-body p-4">
                  <div class="text-center mt-2">
                    <h5 class="text-primary">{{ user && user.is_password_forgot === 1 ? 'Reset Password' : 'Change Password' }}</h5>
                    <p class="text-muted">Setup your password for security.</p>
                  </div>

                  <b-alert v-if="notification.message" 
                    show :variant="notification.type" class="fade show" role="alert">
                      {{ notification.message}}
                  </b-alert>

                  <div class="p-2">
                    <form @submit.prevent="changePassword">
                      <div v-if="user && user.is_password_forgot === 1" class="mb-4">
                        <label class="form-label required">Temporary Password</label>
                        <div class="position-relative auth-pass-inputgroup mb-3">
                          <input
                            :disabled="user && user.is_password_forgot === 1"
                            autofocus
                            :type="temporaryPasswordInputType"
                            v-model="state.temporary_password"
                            class="form-control pe-5"
                            id="temporary_password"
                            :class="{ 'is-invalid': submitted && v$.temporary_password.$error }"
                            placeholder="Enter your temporary password..."
                          />
                          <button
                            class="
                              btn btn-link
                              position-absolute
                              end-0
                              top-0
                              text-decoration-none text-muted
                            "
                            type="button"
                            id="password-addon"
                          >
                            <i @click="showTemporaryPassword" class="ri-eye-fill align-middle"></i>
                          </button>
                          <div
                            v-for="(item, index) in v$.temporary_password.$errors"
                            :key="index"
                            class="invalid-feedback"
                          >
                            <span v-if="item.$message">{{ item.$message }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-if="user && user.is_password_forgot === 0" class="mb-4">
                        <label class="form-label required">Old Password</label>
                        <div class="position-relative auth-pass-inputgroup mb-3">
                          <input
                            autofocus
                            :type="oldPasswordInputType"
                            v-model="state.old_password"
                            class="form-control pe-5"
                            id="old_password"
                            :class="{ 'is-invalid': submitted && v$.old_password.$error }"
                            placeholder="Enter your old password..."
                          />
                          <button
                            class="
                              btn btn-link
                              position-absolute
                              end-0
                              top-0
                              text-decoration-none text-muted
                            "
                            type="button"
                            id="password-addon"
                          >
                            <i @click="showOldPassword" class="ri-eye-fill align-middle"></i>
                          </button>
                          <div
                            v-for="(item, index) in v$.old_password.$errors"
                            :key="index"
                            class="invalid-feedback"
                          >
                            <span v-if="item.$message">{{ item.$message }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="mb-4">
                        <label class="form-label required">New Password</label>
                        <div class="position-relative auth-pass-inputgroup mb-3">
                          <input
                            ref="password"
                            autofocus
                            :type="newPasswordInputType"
                            v-model="state.password"
                            class="form-control pe-5"
                            id="password"
                            :class="{ 'is-invalid': submitted && v$.password.$error }"
                            placeholder="Enter your new password..."
                          />
                          <button
                            class="
                              btn btn-link
                              position-absolute
                              end-0
                              top-0
                              text-decoration-none text-muted
                            "
                            type="button"
                            id="password-addon"
                          >
                            <i @click="showNewPassword" class="ri-eye-fill align-middle"></i>
                          </button>
                          <div
                            v-for="(item, index) in v$.password.$errors"
                            :key="index"
                            class="invalid-feedback"
                          >
                            <span v-if="item.$message">{{ item.$message }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="mb-4">
                        <label class="form-label required">Confirm Password</label>
                        <div class="position-relative auth-pass-inputgroup mb-3">
                          <input
                            :type="confirmPasswordInputType"
                            v-model="state.password_confirmation"
                            class="form-control pe-5"
                            id="password_confirmation"
                            :class="{ 'is-invalid': submitted && v$.password_confirmation.$error }"
                            placeholder="Enter password confirmation..."
                          />
                          <button
                            class="
                              btn btn-link
                              position-absolute
                              end-0
                              top-0
                              text-decoration-none text-muted
                            "
                            type="button"
                            id="password-addon"
                          >
                            <i @click="showConfirmPassword" class="ri-eye-fill align-middle"></i>
                          </button>
                          <div
                            v-for="(item, index) in v$.password_confirmation.$errors"
                            :key="index"
                            class="invalid-feedback"
                          >
                            <span v-if="item.$message">{{ item.$message }}</span>
                            </div>
                          </div>
                      </div>

                      <div class="row mt-4">
                          <div class="col-auto">
                              <button class="btn btn-success w-100" type="submit">
                                  <i class="mdi mdi-content-save-edit mr-1"></i>
                                  {{ user && user.is_password_forgot === 1 ? 'Reset Password' : 'Change Password' }}
                              </button>
                          </div>
                          <div class="col-auto">
               <router-link v-if="user && user.is_password_forgot !== 1" to="/" class="btn btn-danger w-100">
                                  <i class="mdi mdi-close mr-1"></i>
                                  Cancel
                              </router-link>
                          </div>
                      </div>
                    </form>
                    <!-- end form -->
                  </div>
                </div>
                <!-- end card body -->
              </div>
            </b-overlay>
            <!-- end card -->
          </div>
        </div>
        <!-- end row -->
      </div>
      <!-- end container -->
    </div>
    <!-- end auth page content -->

    <!-- footer -->
    <footer class="footer">
      <div class="container">
        <div class="row">
          <div class="col-lg-12">
            <div class="text-center">
              <p class="mb-0 text-muted">
                &copy; {{ new Date().getFullYear() }} QPAX Traffic Systems, Inc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
    <!-- end Footer -->
  </div>
  <!-- end auth-page-wrapper -->
</template>
